// =========================================================
// Vojjin Selection Utilities - v2.3 (DOM-Bound Edition)
// =========================================================
import {registerGlobal} from "./core.js";

class DropdownSelection {
    constructor(containerId, items = {}, options = {}) {
        this.container = document.getElementById(containerId);
        this.items = items;
        this.options = options;
        this.selectedKeys = new Set();
        this.single = !!this.options.single;
        this.itemTemplate = this.options.itemTemplate || '<p data-id="{KEY}">{VALUE}</p>';
        this.groupTemplate = this.options.groupTemplate || '<p style="text-align: center; color:#aaa;">{VALUE}</p>';
        // Keys flagged as group headers (non-selectable). Coerced to strings
        // because `this.items` keys are always strings on JS side.
        this.groupKeys = new Set((this.options.groupKeys || []).map(String));
        // Keys flagged as children-of-a-group (get .has-parent indent).
        this.childKeys = new Set((this.options.childKeys || []).map(String));
        this.showSearch = !!this.options.showSearch;
        this.focusIndex = 0;
        this.eventsHalted = false;
        this._render();

        // 🔹 Define live value property on container
        Object.defineProperty(this.container, "value", {
            configurable: true,
            get: () => this.getValueAsString(),
            set: (val) => {
                // accept either string or array
                if (this.single) {
                    this.setValue(val);
                } else if (Array.isArray(val)) {
                    this.setValue(val);
                } else if (typeof val === "string") {
                    const arr = val.split(",").map(v => v.trim()).filter(Boolean);
                    this.setValue(arr);
                }
            }
        });
    }

    _render() {
        this.container.innerHTML = "";

        this.display = document.createElement("div");
        this.display.className = "selection-display";
        this.placeholder = document.createElement("span");
        this.placeholder.className = "selection-placeholder";
        this.placeholder.innerText = this.options.placeholder || (this.single ? "Select one..." : "Select options...");
        this.display.appendChild(this.placeholder);

        // 🔹 Hidden input (optional for forms)
        this.hiddenInput = document.createElement("input");
        this.hiddenInput.type = "hidden";
        this.hiddenInput.name = this.options.name || this.container.id || "selection";
        this.container.appendChild(this.hiddenInput);

        this.dropdownMenu = document.createElement("div");
        this.dropdownMenu.className = "selection-menu-custom";

        if (!this.single) {
            const header = document.createElement("div");
            header.className = "selection-header";
            const btnAll = document.createElement("button");
            btnAll.className = "selection-btn select-all";
            btnAll.innerText = "Select All";
            const btnClear = document.createElement("button");
            btnClear.className = "selection-btn clear-all";
            btnClear.innerText = "Clear All";
            header.append(btnAll, btnClear);
            this.dropdownMenu.appendChild(header);

            btnAll.addEventListener("click", (e) => {
                e.stopPropagation();
                Object.keys(this.items).forEach((k) => this.selectedKeys.add(k));
                this._updateSelectionState();
                this._renderTags();
                this._syncValue();
                this._emitChange();
                this.close();
            });

            btnClear.addEventListener("click", (e) => {
                e.stopPropagation();
                this.clear();
                this.close();
            });
        }
        if (this.showSearch) {
            this.searchBox = document.createElement("input");
            this.searchBox.type = "text";
            this.searchBox.className = "selection-search";
            this.searchBox.placeholder = "Pretraži...";
            this.dropdownMenu.appendChild(this.searchBox);
        }

        this.itemsContainer = document.createElement("div");
        this.dropdownMenu.appendChild(this.itemsContainer);

        this._rebuild();
        /*Object.entries(this.items).forEach(([key, value], index) => {
            const item = document.createElement("div");
            item.className = "selection-item-custom";
            item.dataset.key = key;
            item.dataset.index = index;
            item.innerHTML = Utils.replaceAll(this.itemTemplate, ["{KEY}", "{VALUE}"], [key, value]);
            this.itemsContainer.appendChild(item);
            item.addEventListener("click", () => this._toggleSelection(key, item));
        });*/

        this.display.addEventListener("click", () => this.toggle());
        if (this.showSearch) {
            this.searchBox.addEventListener("keyup", () => this._filterItems());
        }
        document.addEventListener("click", (e) => {
            if (!e.target.closest(`#${this.container.id}`)) this.close();
        });

        this.display.tabIndex = 0;
        //this.display.addEventListener("keydown", (e) => this._handleKeyboard(e));

        this.container.append(this.display, this.dropdownMenu);
    }

    _toggleSelection(key, item) {
        if (this.single) {
            this.selectedKeys.clear();
            this.itemsContainer.querySelectorAll(".selection-item-custom").forEach(i => i.classList.remove("selected"));
            this.selectedKeys.add(key);
            item.classList.add("selected");
            this._renderTags();
            this._syncValue();
            this._emitChange(true);
            this.close();
            return;
        }

        if (this.selectedKeys.has(key)) {
            this.selectedKeys.delete(key);
            item.classList.remove("selected");
        } else {
            this.selectedKeys.add(key);
            item.classList.add("selected");
        }
        this._renderTags();
        this._syncValue();
        this._emitChange(true);
    }

    _renderTags() {
        this.display.innerHTML = "";

        if (this.selectedKeys.size === 0) {
            this.display.appendChild(this.placeholder);
            return;
        }

        // 🟦 SINGLE SELECT MODE
        if (this.single) {
            const key = Array.from(this.selectedKeys)[0];
            const label = this.items[key] || "";
            const span = document.createElement("span");
            span.className = "selection-single-label";
            span.innerText = label;
            this.display.appendChild(span);
            return;
        }

        // 🟩 MULTI SELECT MODE
        const mode = this.options.displayMode || "tags";

        if (mode === "list") {
            // new compact list display
            const labels = Array.from(this.selectedKeys).map(k => this.items[k]);
            const text = labels.join(", ");
            const span = document.createElement("span");
            span.className = "selection-list-display";
            span.title = text; // full tooltip
            span.innerText = text;
            this.display.appendChild(span);
        } else {
            // default: tag view
            this.selectedKeys.forEach((key) => {
                const tag = document.createElement("div");
                tag.className = "selection-tag";
                tag.innerHTML = `<span>${this.items[key]}</span><span class="remove">&times;</span>`;
                tag.querySelector(".remove").addEventListener("click", (e) => {
                    e.stopPropagation();
                    this._removeKey(key);
                });
                this.display.appendChild(tag);
            });
        }
    }

    _removeKey(key) {
        this.selectedKeys.delete(key);
        this._updateSelectionState();
        this._renderTags();
        this._syncValue();
        this._emitChange();
    }

    _updateSelectionState() {
        this.itemsContainer.querySelectorAll(".selection-item-custom").forEach((item) => {
            const key = item.dataset.key;
            item.classList.toggle("selected", this.selectedKeys.has(key));
        });
    }

    _syncValue() {
        const value = this.getValueAsString();
        this.hiddenInput.value = value;
        this.container.dataset.selectionValue = value;
        this.container.setAttribute("value", value); // HTML attribute for reading
    }

    getValue() {
        return this.single ? Array.from(this.selectedKeys)[0] || null : Array.from(this.selectedKeys);
    }

    getValueAsString() {
        return this.single ? (Array.from(this.selectedKeys)[0] || "") : Array.from(this.selectedKeys).join(",");
    }

    setValue(value, silent = true) {
        if (!silent) this.haltEvents();
        this.clear(true);
        if (this.single) {
            if (value && this.items[value] && !this.groupKeys.has(String(value))) this.selectedKeys.add(value);
        } else if (Array.isArray(value)) {
            value.forEach(v => {
                if (this.items[v] && !this.groupKeys.has(String(v))) this.selectedKeys.add(v);
            });
        }
        this._updateSelectionState();
        this._renderTags();
        this._syncValue();
        if (!silent) {
            this.resumeEvents();
            this._emitChange(true);
        }
    }

    _filterItems() {
        const term = this.searchBox.value.toLowerCase();
        this.itemsContainer.querySelectorAll(".selection-item-custom").forEach((item) => {
            const text = item.textContent.toLowerCase();
            item.style.display = text.includes(term) ? "flex" : "none";
        });
    }

    clear(silent = false) {
        this.selectedKeys.clear();
        this._updateSelectionState();
        this._renderTags();
        this._syncValue();
        if (!silent) this._emitChange();
    }

    _emitChange(dispatch = false) {
        if (this.eventsHalted) return;
        if (this.options.onChange) this.options.onChange(this.getValue());
        if (dispatch === true) {
            const event = new Event("change", {bubbles: true});
            this.container.dispatchEvent(event);
        }
    }

    haltEvents() {
        this.eventsHalted = true;
    }

    resumeEvents() {
        this.eventsHalted = false;
    }

    triggerChange(dispatch = true) {
        this._emitChange(dispatch);
    }

    updateItems(newItems = {}, silent = false) {
        if (silent) this.haltEvents();
        this.items = newItems;
        this.selectedKeys.clear();

        // Clear dropdown content
        if (this.itemsContainer) this.itemsContainer.innerHTML = "";

        // Rebuild list
        this._rebuild();

        // Update display and sync state
        this._renderTags();
        this._syncValue();

        if (silent) {
            this.resumeEvents();
        } else {
            this._emitChange(true);
        }
    }

    _rebuild() {
        // Rebuild list.
        // Prefer the explicit itemOrder when supplied — JS objects iterate
        // integer-like keys numerically, which destroys PHP-side ordering.
        const order = Array.isArray(this.options.itemOrder) && this.options.itemOrder.length
            ? this.options.itemOrder.map(String)
            : Object.keys(this.items);
        order.forEach((key, index) => {
            if (!(key in this.items)) return;
            const value = this.items[key];
            if (this.groupKeys.has(String(key))) {
                // Group header (non-selectable). Uses a distinct class so
                // existing `.selection-item-custom` query selectors used by
                // filter/click/selection-state logic naturally skip these.
                const item = document.createElement("div");
                item.className = "selection-group-custom";
                item.dataset.key = key;
                item.dataset.index = index;
                item.innerHTML = Utils.replaceAll(this.groupTemplate, ["{KEY}", "{VALUE}"], [key, value]);
                this.itemsContainer.appendChild(item);
            } else if (key.startsWith("__")) {
                const item = document.createElement("div");
                item.className = "selection-item-custom";
                item.dataset.key = key;
                item.dataset.index = index;
                item.innerHTML = Utils.replaceAll(this.groupTemplate, ["{KEY}", "{VALUE}"], [key, value]);
                this.itemsContainer.appendChild(item);
            } else {
                const item = document.createElement("div");
                item.className = "selection-item-custom" + (this.childKeys.has(String(key)) ? " has-parent" : "");
                item.dataset.key = key;
                item.dataset.index = index;
                item.innerHTML = Utils.replaceAll(this.itemTemplate, ["{KEY}", "{VALUE}"], [key, value]);
                this.itemsContainer.appendChild(item);
                item.addEventListener("click", () => this._toggleSelection(key, item));
            }
        });
    }

    open() {
        if (!this.dropdownMenu) return;

        // === 1️⃣ Temporarily show off-screen for measurement ===
        this.dropdownMenu.style.visibility = "hidden";
        this.dropdownMenu.style.display = "block";
        this.dropdownMenu.style.maxHeight = "none"; // ensure full measurement

        const rect = this.container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = this.dropdownMenu.scrollHeight || 200; // default fallback if 0

        // === 2️⃣ Restore to hidden before real show ===
        this.dropdownMenu.style.visibility = "";
        this.dropdownMenu.style.display = "";
        this.dropdownMenu.style.maxHeight = "300px";

        console.log("Viewport:", viewportHeight, "Dropdown height:", dropdownHeight, "Top:", rect.top, "Bottom:", rect.bottom);

        let flipUp = false;

        if (this.options.autoFlip) {
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            flipUp = (spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
        }

        // === 3️⃣ Apply real visual positioning ===
        if (flipUp) {
            this.dropdownMenu.classList.add("flip-up");
            this.dropdownMenu.style.top = "auto";
            this.dropdownMenu.style.bottom = `${rect.height}px`;
        } else {
            this.dropdownMenu.classList.remove("flip-up");
            this.dropdownMenu.style.bottom = "auto";
            this.dropdownMenu.style.top = "100%";
        }

        // === 4️⃣ Show dropdown ===
        this.dropdownMenu.classList.add("show");
        if (this.showSearch) {
            this.searchBox.focus();
        }

        if (this.options.onOpen) this.options.onOpen();
    }

    close() {
        if (!this.dropdownMenu.classList.contains("show")) return; // 👈 Prevent ghost closes
        this.dropdownMenu.classList.remove("show", "flip-up");
        this.dropdownMenu.style.top = "100%";
        this.dropdownMenu.style.bottom = "auto";
        if (this.options.onClose) this.options.onClose();
    }

    toggle() {
        const isOpen = this.dropdownMenu.classList.contains("show");
        if (isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    destroy() {
        this.container.innerHTML = "";
        this.selectedKeys.clear();
        delete this.container.value;
    }
}

registerGlobal("DropdownSelection", DropdownSelection);